import { useErrorHandlingSlice } from 'app/error/slice';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import useApi from 'services/mavapi/useApi';
import {
  ManageTeamService,
  RiderWrapper,
  TeamsWrapper,
  TeamRequest,
  TeamService,
  TeamRegistration,
} from 'services/openapi';
import { useNavigateToRoute } from 'utils/common';
import isApiError from 'utils/isApiError';
import Cookies from 'js-cookie';

const useTeamApi = () => {
  const userType = Cookies.get('userType') ?? '';
  const [loading, setLoading] = useState(false);
  const [, setError] = useState<unknown | null>();
  const dispatch = useDispatch();
  const { action } = useParams();
  const { actions } = useErrorHandlingSlice();
  const { error: apiError, handleRequest } = useApi();
  const [teamRiderData, setTeamRiderData] = useState<RiderWrapper>();
  const {
    manageTeamGetTeams,
    manageTeamGetRiders,
    manageTeamCreateOrEditTeam,
    manageTeamUploadTeamImage,
    manageTeamMakeTeamActiveOrInactive,
    manageTeamDeleteTeam,
    manageTeamUpdateRiderStatus,
    manageTeamDeleteRiderInfo,
    manageTeamExportTeamsList,
  } = ManageTeamService;
  const [teamProfileData, setTeamProfileData] = useState<TeamsWrapper>();
  const [riderList, setRiderList] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [imageUploadBlobPayload, setImageUploadBlobPayload] =
    useState<Blob | null>(null);
  const redirect = useNavigateToRoute();
  const { teamGenerateTeamCode, teamGetTeam } = TeamService;
  const [teamCodeValue, setTeamCodeValue] = useState<string>();
  const [teamDetailsData, setTeamDetailsData] = useState<TeamRegistration>();
  const [controller, setController] = useState(new AbortController());
  const handleDownloadTeam = (
    searchInput?: string,
    status?: string,
    seasonId?: string,
    sortBy?: string,
    sortOrder?: number,
    paidFilter?: number,
  ) => {
    setDownloadLoading(true);
    return new Promise((resolve, reject) => {
      handleRequest(
        manageTeamExportTeamsList(
          searchInput,
          status,
          seasonId,
          paidFilter,
          sortBy,
          sortOrder,
        ),
      )
        .then(res => {
          dispatch(
            actions.dispatchToast({
              errorTxt: 'Downloaded Successfully',
              show: true,
              errorType: 'success',
            }),
          );
          resolve(res);
          setDownloadLoading(false);
          setLoading(false);
        })
        .catch(() => {
          setDownloadLoading(false);
          reject(apiError);
          setLoading(false);
        });
    });
  };
  const fetchManageGetTeams = (
    searchInput?: string,
    status?: string,
    seasonId?: string,
    sortBy?: string,
    sortOrder?: number,
    pageNumber: number = 0,
    pageSize: number = 10,
    paidFilter?: number,
  ) => {
    controller.abort(); // Abort previous request
    const newController = new AbortController();
    setController(newController);
    setLoading(true);
    let pageNumCall = pageNumber ? Math.floor(pageNumber / pageSize) + 1 : 1;
    handleRequest(
      manageTeamGetTeams(
        searchInput,
        status,
        seasonId,
        paidFilter,
        sortBy,
        sortOrder,
        pageNumCall,
        pageSize,
      ),
      newController.signal,
    )
      .then(res => {
        if (res) {
          setTeamProfileData(res);
        }
        setLoading(false);
      })
      .catch(() => {
        setError(apiError);
        setLoading(false);
      });
  };
  const createTeamProfile = (requestBody?: TeamRequest, blob?: Blob) => {
    const formData = {
      file: blob,
    };
    setLoading(true);
    handleRequest(manageTeamCreateOrEditTeam(requestBody))
      .then(res => {
        if (typeof res === 'string') {
          if (blob) {
            teamImageUpload(res ?? '', formData, action);
          } else {
            if (userType === 'COACH') {
              redirect(`/coach/teams/${`view`}/profile-info/${res}`);
            } else {
              redirect(`/admin/teams/${`view`}/profile-info/${res}`);
            }
            if (action === 'add') {
              dispatch(
                actions.dispatchToast({
                  errorTxt: 'Team Created Successfully',
                  show: true,
                  errorType: 'success',
                }),
              );
            } else if (action === 'edit') {
              dispatch(
                actions.dispatchToast({
                  errorTxt: 'Team Updated Successfully',
                  show: true,
                  errorType: 'success',
                }),
              );
            }
          }
        }
        setLoading(false);
      })
      .catch(() => {
        setError(apiError);
        setLoading(false);
      });
  };
  const teamImageUpload = (
    id: string,
    formData?: {
      file?: Blob;
    },
    action?: string,
  ) => {
    setLoading(true);
    handleRequest(manageTeamUploadTeamImage(id, formData))
      .then(res => {
        if (userType === 'COACH') {
          redirect(`/coach/teams/${`view`}/profile-info/${id}`);
        } else {
          redirect(`/admin/teams/${`view`}/profile-info/${id}`);
        }
        setImageUploadBlobPayload(null);
        if (action === 'add') {
          dispatch(
            actions.dispatchToast({
              errorTxt: 'Team Created Successfully',
              show: true,
              errorType: 'success',
            }),
          );
        } else if (action === 'edit') {
          dispatch(
            actions.dispatchToast({
              errorTxt: 'Team Edited Successfully',
              show: true,
              errorType: 'success',
            }),
          );
        }
        fetchManageGetTeams();
        setLoading(false);
      })
      .catch(() => {
        setError(apiError);
        setLoading(false);
      });
  };

  const teamActiveOrInactive = (
    id: string,
    activeStatusId?: string,
    seasonId?: string,
    searchInput?: string,
    status?: string,
    sortBy?: string,
    sortOrder?: number,
    pageNumber: number = 0,
    pageSize: number = 10,
    item?: string,
  ) => {
    setLoading(true);
    handleRequest(
      manageTeamMakeTeamActiveOrInactive(id, activeStatusId, seasonId),
    )
      .then(res => {
        if (res) {
          fetchManageGetTeams(
            searchInput,
            status,
            seasonId,
            sortBy,
            sortOrder,
            pageNumber,
            pageSize,
          );
          if (item === 'Make active') {
            dispatch(
              actions.dispatchToast({
                errorTxt: 'Successfully Active',
                show: true,
                errorType: 'success',
              }),
            );
          } else {
            dispatch(
              actions.dispatchToast({
                errorTxt: 'Successfully Inactive',
                show: true,
                errorType: 'success',
              }),
            );
          }
        }
        setLoading(false);
      })
      .catch(() => {
        setError(apiError);
        setLoading(false);
      });
  };

  const deleteTeam = (
    id?: string,
    searchInput?: string,
    status?: string,
    seasonId?: string,
    sortBy?: string,
    sortOrder?: number,
    pageNumber: number = 0,
    pageSize: number = 10,
  ) => {
    if (!id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    handleRequest(manageTeamDeleteTeam(id))
      .then(() => {
        setLoading(false);
        dispatch(
          actions.dispatchToast({
            errorTxt: 'Team deleted successfully',
            show: true,
            errorType: 'success',
          }),
        );
        // Calculate the correct page after deletion
        // If current page is empty after deletion, go to previous page
        const itemsOnCurrentPage = teamProfileData?.teams?.length ?? 0;
        let nextPageNumber = pageNumber;

        // If we're on a page with only 1 item and we just deleted it, go to previous page
        if (itemsOnCurrentPage <= 1 && pageNumber > 0) {
          nextPageNumber = pageNumber - pageSize;
        }

        fetchManageGetTeams(
          searchInput,
          status,
          seasonId,
          sortBy,
          sortOrder,
          nextPageNumber,
          pageSize,
        );
      })
      .catch(apiError => {
        setError(apiError);
        setLoading(false);
      });
  };
  const fetchTeamRiderList = (
    id: string,
    searchInput?: string,
    season?: string,
    status?: string,
    sortBy?: string,
    sortOrder?: number,
    pageNumber: number = 0,
    pageSize: number = 10,
  ) => {
    setLoading(true);
    let pageNumCall = pageNumber ? Math.floor(pageNumber / pageSize) + 1 : 1;
    handleRequest(
      manageTeamGetRiders(
        id,
        searchInput,
        season,
        status,
        sortBy,
        sortOrder,
        pageNumCall,
        pageSize,
      ),
    )
      .then(res => {
        if (res) {
          setTeamRiderData(res);
        }
        setLoading(false);
      })
      .catch(() => {
        setError(apiError);
        setLoading(false);
      });
  };

  const teamRiderActiveOrInactive = (
    id: string,
    teamId?: string,
    statusId?: string,
    seasonId?: string,
    searchInput?: string,
    status?: string,
    sortBy?: string,
    sortOrder?: number,
    pageNumber: number = 0,
    pageSize: number = 10,
    item?: string,
  ) => {
    setLoading(true);
    handleRequest(manageTeamUpdateRiderStatus(id, statusId))
      .then(res => {
        if (res) {
          fetchTeamRiderList(
            teamId ?? '',
            searchInput,
            '',
            status,
            sortBy,
            sortOrder,
            pageNumber,
            pageSize,
          );
          if (item === 'Make active') {
            dispatch(
              actions.dispatchToast({
                errorTxt: 'Successfully Active',
                show: true,
                errorType: 'success',
              }),
            );
          } else {
            dispatch(
              actions.dispatchToast({
                errorTxt: 'Successfully Inactive',
                show: true,
                errorType: 'success',
              }),
            );
          }
        }
        setLoading(false);
      })
      .catch(() => {
        setError(apiError);
        setLoading(false);
      });
  };
  const deleteTeamRiderList = (id?: string) => {
    setLoading(true);
    handleRequest(manageTeamDeleteRiderInfo(id ?? ''))
      .then(res => {
        if (res) {
          dispatch(
            actions.dispatchToast({
              errorTxt: 'Rider deleted successfully',
              show: true,
              errorType: 'success',
            }),
          );
          setRiderList(!riderList);
        }
        setLoading(false);
      })
      .catch(apiError => {
        setError(apiError);
        setLoading(false);
      });
  };
  const generateTeamCode = (teamName?: string) => {
    handleRequest(teamGenerateTeamCode(teamName))
      .then(res => {
        if (res && !isApiError(res)) {
          setTeamCodeValue(res);
        } else {
          setTeamCodeValue(res.code);
        }
      })
      .catch(() => setError(apiError));
  };
  const getSelectedTeamDetailsById = (teamId: string) => {
    handleRequest(teamGetTeam(teamId))
      .then(res => {
        setTeamDetailsData(res)
      })
      .catch(() => setError(apiError));
  };
  return {
    fetchManageGetTeams,
    teamProfileData,
    loading,
    fetchTeamRiderList,
    teamRiderData,
    createTeamProfile,
    teamImageUpload,
    deleteTeam,
    teamActiveOrInactive,
    teamRiderActiveOrInactive,
    deleteTeamRiderList,
    riderList,
    setImageUploadBlobPayload,
    imageUploadBlobPayload,
    setTeamProfileData,
    teamCodeValue,
    generateTeamCode,
    setTeamCodeValue,
    handleDownloadTeam,
    downloadLoading,
    getSelectedTeamDetailsById,
    teamDetailsData,
    setTeamDetailsData
  };
};

export default useTeamApi;
